export interface Product {
  name: string
  slug: string
  description: string
  image: string
  images?: string[]
  pricePerYard: number
}

export interface Category {
  name: string
  description: string
  image: string
  products: Product[]
}

export const categories: Category[] = [
  {
    name: 'Base & Compaction',
    description:
      'Angular, crushed stone and recycled materials that lock together and compact into a solid, load-bearing surface. The go-to choices for driveways, parking pads, road bases, walkways, and any project that needs a stable foundation.',
    image:
      'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=800&h=400&fit=crop',
    products: [
      {
        name: '1.25" Minus',
        slug: '1-25-minus',
        description:
          'A coarser crushed rock with pieces up to 1.25 inches, blended with smaller fines for excellent compaction. This is your heavy-duty base layer — perfect for building up driveways, establishing road base, stabilizing parking areas, and providing structural support under concrete or pavers. Often used as the first layer before topping with 5/8" Minus.',
        image:
          'https://cdn.magicpatterns.com/uploads/9E85amw56n3D8ydgKmAG2Z/1_25_minus_gravel.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/9E85amw56n3D8ydgKmAG2Z/1_25_minus_gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/7VsC9jjSWLbwV6Us3bXNhz/inch_25_minus2.png',
        ],
        pricePerYard: 52,
      },
      {
        name: '5/8" Minus',
        slug: '5-8-minus',
        description:
          'Our most popular driveway gravel. This finely crushed, angular stone includes a mix of particles down to dust size, which allows it to pack down tight and create a smooth, firm surface. Ideal for driveways, walkways, patio bases, and as a top layer over coarser base rock. Compacts beautifully with minimal maintenance.',
        image:
          'https://cdn.magicpatterns.com/uploads/uJHdEMdk8Ku5nMisWk411s/58minus.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/uJHdEMdk8Ku5nMisWk411s/58minus.png',
          'https://cdn.magicpatterns.com/uploads/7QxRMTLFhKZH6A9aYsWUw1/PXL_20260422_184444516.PORTRAIT~2.jpg',
          'https://cdn.magicpatterns.com/uploads/7G9YZaEtyKVu5ab5dYbZxn/PXL_20260422_184432798.PORTRAIT~2.jpg',
        ],
        pricePerYard: 52,
      },
      {
        name: 'Walkway Gravel',
        slug: '3-8-minus-driveway',
        description:
          'A finer crushed rock with angular pieces up to 3/8 inch, blended with smaller fines for a tight, smooth finish. Compacts exceptionally well, making it a top choice for driveways, walkways, and patios where you want a clean, packed surface with minimal loose stone. Easy to spread and maintain.',
        image:
          'https://cdn.magicpatterns.com/uploads/cNV5HRvifStWK1UGC5diuK/walkway_gravel_253.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/cNV5HRvifStWK1UGC5diuK/walkway_gravel_253.png',
          'https://cdn.magicpatterns.com/uploads/cGVEgK2FWiTt371km5oGeG/file_0000000030b471fd97f2b00e45cd8c0e.png',
          'https://cdn.magicpatterns.com/uploads/7tucQVL97E7dTm2FTUp6bj/PXL_20260414_153527381.jpg',
        ],
        pricePerYard: 50,
      },
      {
        name: 'Recycled Asphalt',
        slug: 'recycled-asphalt',
        description:
          'Milled and crushed asphalt from road and parking lot projects. The residual tar in the material causes it to re-bind over time, especially in warm weather, creating a semi-hard surface that resists erosion. Popular for rural driveways, farm roads, temporary construction access, and as a cost-effective alternative to fresh asphalt paving.',
        image:
          'https://cdn.magicpatterns.com/uploads/pTVDr44Wtojj8Anp2QQMDM/image.png',
        pricePerYard: 35,
      },
      {
        name: 'Recycled Concrete',
        slug: 'recycled-concrete',
        description:
          'Crushed, screened, and sized concrete reclaimed from local demolition projects. Compacts well and provides a stable, load-bearing base similar to virgin crushed rock. A smart, affordable choice for road base, driveway sub-base, backfill behind walls, and utility trench bedding. Keeps usable material out of the landfill while saving you money.',
        image:
          'https://cdn.magicpatterns.com/uploads/rAceDaqeD4rJw2Yc7jJZMJ/recycled_concrete_tacoma.png',
        pricePerYard: 35,
      },
      {
        name: 'Builders Sand',
        slug: 'builders-sand',
        description:
          'Coarse, washed sand used in construction and landscaping. Essential for mixing concrete and mortar, leveling paver bases, filling horseshoe pits, and creating sand-set patios. Also works well as pipe bedding for utility trenches and as a leveling layer under above-ground pools. Clean and consistent grain size for reliable results.',
        image:
          'https://cdn.magicpatterns.com/uploads/t2AoqCyGNHu1JXzJkEpNao/utility_sand.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/t2AoqCyGNHu1JXzJkEpNao/utility_sand.jpg',
          'https://cdn.magicpatterns.com/uploads/6b7xRasw6uhuMVuaj8v1VG/builders_sand.png',
        ],
        pricePerYard: 58,
      },
    ],
  },
  {
    name: 'Clean & Drain Rock',
    description:
      'Washed stone with no fines or dust — water flows freely through the gaps. The right choice for French drains, curtain drains, septic fields, retaining wall backfill, and any project where drainage performance is critical.',
    image:
      'https://images.unsplash.com/photo-1615811361523-6bd03d7748e7?w=800&h=400&fit=crop',
    products: [
      {
        name: '5/8" Clean',
        slug: '5-8-clear',
        description:
          'Clean, washed crushed stone with no fines or dust — just angular 5/8" pieces. Because there are no fine particles to clog the gaps, water flows freely through it, making this the preferred choice for French drains, curtain drains, and drainage trenches. Also works well as pipe bedding and behind retaining walls where water management is critical.',
        image:
          'https://cdn.magicpatterns.com/uploads/bXdm32kexBvq2fSGoYeg5W/58clean.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/bXdm32kexBvq2fSGoYeg5W/58clean.png',
          'https://cdn.magicpatterns.com/uploads/96U9gsJQpdtAbH8LKxzSbp/image.png',
        ],
        pricePerYard: 70,
      },
      {
        name: '7/8" Drain Rock',
        slug: '7-8-drain-rock',
        description:
          'Clean, rounded rock sized at approximately 7/8 inch — the standard spec for residential French drains throughout the Puget Sound region. Washed free of fines so water passes through easily. Wrap it in filter fabric around your perforated pipe for a textbook drainage installation. Also commonly used for septic systems and as a base layer under decks.',
        image:
          'https://cdn.magicpatterns.com/uploads/4CMhgoaosJRQ9X87j7Wdzf/7_8_drain_rock_delivery.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/4CMhgoaosJRQ9X87j7Wdzf/7_8_drain_rock_delivery.png',
          'https://cdn.magicpatterns.com/uploads/rjg9Ff86Mr2rsdPCf5fYSM/drain_rock_small.png',
        ],
        pricePerYard: 62,
      },
      {
        name: '1.25" Clean',
        slug: '1-25-clear',
        description:
          'Larger washed angular stone, screened to remove all fines. The bigger gaps between stones allow for maximum water flow, making this ideal for heavy-duty drainage applications, septic drain fields, retaining wall backfill, and large-scale stormwater management. A workhorse material for any project where moving water away fast is the priority.',
        image:
          'https://cdn.magicpatterns.com/uploads/iyKcHiQf7jUMcnbtPH51fD/125.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/iyKcHiQf7jUMcnbtPH51fD/125.png',
          'https://cdn.magicpatterns.com/uploads/wwAR73RWBJ4Vnm85vU5YtU/image.png',
        ],
        pricePerYard: 62,
      },
      {
        name: '1.5" Drain Rock',
        slug: '1-5-drain-rock',
        description:
          'Larger rounded drain rock for projects that need to move serious water. The bigger stone size creates larger voids for maximum flow capacity, making it the right call for commercial drainage, large septic drain fields, retaining wall weep systems, and anywhere you need heavy-duty water management. Clean-washed with no fines to restrict flow.',
        image:
          'https://cdn.magicpatterns.com/uploads/sofXYMduQmUFzGgj6FJJFX/inch_quarter_drain_rock.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/sofXYMduQmUFzGgj6FJJFX/inch_quarter_drain_rock.png',
          'https://cdn.magicpatterns.com/uploads/me3fE9s81YnndVtj12ocF9/drain_rock_large_253gravel.png',
        ],
        pricePerYard: 69,
      },
      {
        name: 'Pea Gravel',
        slug: 'pea-gravel',
        description:
          'Small, smooth, naturally rounded stones roughly the size of a pea (about 3/8"). Available in a mix of earth tones — tans, grays, whites, and browns. A versatile favorite for garden pathways, play areas, dog runs, patio fill between pavers, and decorative ground cover. Drains well and feels comfortable underfoot. Not recommended for driveways as it doesn\'t compact.',
        image:
          'https://cdn.magicpatterns.com/uploads/1dngQ871musNM79M3FRfD6/pea_gravel_253_gravel.jpg',
        pricePerYard: 54,
      },
    ],
  },
  {
    name: 'Soil, Mulch & Org.',
    description:
      "Premium soil blends, bark mulch, and fill materials for growing, grading, and finishing. Whether you're starting a new lawn, amending garden beds, or giving landscape areas a clean, maintained look.",
    image:
      'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800&h=400&fit=crop',
    products: [
      {
        name: '3-Way Topsoil',
        slug: '3-way-topsoil',
        description:
          'Our premium growing blend — a carefully balanced mix of screened topsoil, aged compost, and coarse sand. The topsoil provides structure, the compost delivers nutrients and beneficial microbes, and the sand ensures proper drainage. Ideal for establishing new lawns, filling raised garden beds, top-dressing existing turf, and any planting project where you want things to thrive from day one.',
        image:
          'https://cdn.magicpatterns.com/uploads/8yZLn1ArqywEa2vWNptiBJ/3way_topsoil_253gravel.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/8yZLn1ArqywEa2vWNptiBJ/3way_topsoil_253gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/2aMuTuHRRFN73t56LgdPvq/3way_soil.png',
        ],
        pricePerYard: 43,
      },
      {
        name: '50/50 Mix',
        slug: '50-50-mix',
        description:
          'A rich 50/50 blend of screened topsoil and aged compost, packed with organic matter and nutrients. This is the go-to mix for amending heavy clay soils common in the Puget Sound area, revitalizing tired garden beds, and boosting soil health in vegetable gardens and flower beds. Lighter and more nutrient-dense than straight topsoil.',
        image:
          'https://cdn.magicpatterns.com/uploads/2WbQ2Ev6zgb1V371W4K46u/50_50_mix_topsoil_tacoma.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/2WbQ2Ev6zgb1V371W4K46u/50_50_mix_topsoil_tacoma.png',
          'https://cdn.magicpatterns.com/uploads/9V5jP2dD5DeuoChaZYeoHk/5050mix_253gravel.jpg',
        ],
        pricePerYard: 51,
      },
      {
        name: 'Fill Dirt',
        slug: 'fill-dirt',
        description:
          "Clean, screened fill dirt for non-growing applications. Use it to raise grades, backfill foundations, fill in holes and low spots, or build up areas before laying gravel or sod. This is subsoil — it won't grow a lawn on its own, but it's the most affordable way to move earth and establish proper drainage slopes on your property.",
        image:
          'https://cdn.magicpatterns.com/uploads/mvue4Ss3PpCv2evpn9ouKp/fill_dirt_253_gravel.png',
        pricePerYard: 16,
      },
      {
        name: 'Fine Bark',
        slug: 'fine-bark',
        description:
          'Finely shredded bark mulch with a smooth, uniform texture. The small pieces create a dense, tidy layer that hugs the soil closely, making it ideal for detailed garden beds, flower borders, and areas where a polished, manicured look is desired. Excellent moisture retention and weed suppression in a refined package.',
        image:
          'https://cdn.magicpatterns.com/uploads/jwh8xDZWrcPmgQp9pamYae/Fine-Bark-253gravel.jpg',
        pricePerYard: 45,
      },
      {
        name: 'Medium Bark',
        slug: 'medium-bark',
        description:
          'The most popular bark mulch in the Pacific Northwest. Medium-sized shredded bark pieces (roughly 1–3 inches) that lay flat, knit together well, and resist washing away on slopes. Perfect for flower beds, garden borders, around trees and shrubs, and anywhere you want a tidy, natural look. Breaks down slowly to feed the soil underneath over time.',
        image:
          'https://cdn.magicpatterns.com/uploads/3bcz4ffHhDoYURGgMbiJcq/medium_bark.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/uXAWFwyecXMMiXVD3PBG8P/medium_beauty_bark_253gravel.jpg',
          'https://cdn.magicpatterns.com/uploads/3bcz4ffHhDoYURGgMbiJcq/medium_bark.png',
        ],
        pricePerYard: 45,
      },
      {
        name: 'Coarse Bark',
        slug: 'coarse-bark',
        description:
          'Chunky, long-lasting bark nuggets (3–5 inches) that hold up well in high-traffic areas and exposed locations. The larger pieces decompose more slowly than medium bark, meaning fewer top-ups and lower long-term cost. Excellent for large landscape beds, commercial properties, hillside erosion control, and anywhere you want bold texture with maximum weed suppression.',
        image:
          'https://cdn.magicpatterns.com/uploads/wtRPmXAqzBUairA8ZDLYNy/coarse_bark_tacoma_delivery.png',
        pricePerYard: 45,
      },
    ],
  },
  {
    name: 'Landscape & Decor',
    description:
      'Naturally rounded, smooth stones and accent rock for landscaping projects that need a premium, eye-catching finish. Perfect for garden beds, dry creek beds, water features, and modern xeriscaping.',
    image:
      'https://images.unsplash.com/photo-1516808468759-390b1e428c0c?w=800&h=400&fit=crop',
    products: [
      {
        name: 'Streambed Cobble',
        slug: 'streambed-cobble',
        description:
          'Naturally rounded, water-tumbled cobblestones in a mix of sizes and earthy tones — grays, tans, and browns. Sourced from regional riverbeds, these stones bring a rugged, natural aesthetic to dry creek beds, drainage swales, water features, and landscape borders. Larger than pea gravel but smoother than quarry rock, streambed cobble bridges the gap between decorative and functional. Excellent for erosion control on slopes and as a natural-looking ground cover that requires zero maintenance.',
        image:
          'https://cdn.magicpatterns.com/uploads/5onxHUcUJws2dWn9MZKgbZ/PXL_20260413_181445485~2.jpg',
        images: [
          'https://cdn.magicpatterns.com/uploads/5onxHUcUJws2dWn9MZKgbZ/PXL_20260413_181445485~2.jpg',
          'https://cdn.magicpatterns.com/uploads/2NTKhJCUSWL4NnY8ELthNB/PXL_20260413_181442329.jpg',
        ],
        pricePerYard: 55,
      },
      {
        name: 'River Rock',
        slug: 'river-rock',
        description:
          "Naturally tumbled, smooth stones in a range of sizes (2–6 inches) and earthy color tones. Sourced from regional riverbeds, these stones add a polished, organic look to any landscape. Use them for dry creek beds, water features, border edging, ground cover around trees, and anywhere you want a natural, low-maintenance aesthetic that won't decompose or fade.",
        image:
          'https://cdn.magicpatterns.com/uploads/nqfErfP96HqjncDBfVrrmT/image.png',
        pricePerYard: 76,
      },
      {
        name: 'Lava Rock',
        slug: 'lava-rock',
        description:
          'Lightweight, porous volcanic rock in a deep reddish-brown color. Weighs roughly half as much as regular gravel, making it easy to spread and great for rooftop planters or areas with weight restrictions. The porous texture retains moisture and insulates plant roots. Popular for fire pit surrounds, succulent gardens, modern xeriscaping, and adding bold color contrast to landscape beds.',
        image:
          'https://cdn.magicpatterns.com/uploads/qsjdvgEGrJSuiRhGf62bek/image.png',
        pricePerYard: 84,
      },
      {
        name: 'Decorative Boulders',
        slug: 'decorative-boulders',
        description:
          'Large natural accent stones ranging from 1 to 4+ feet, hand-selected for character and color. Use them as landscape focal points, natural retaining features, driveway borders, or garden anchors. Each boulder is unique — call us to discuss sizes, colors, and placement. Delivery includes careful placement with our equipment if needed.',
        image:
          'https://cdn.magicpatterns.com/uploads/3wFRErKrysXKJ3WCUAMvCA/decorative_rock_tacoma.png',
        pricePerYard: 105,
      },
    ],
  },
  {
    name: 'Quarry Rock',
    description:
      'Large, angular quarry stone for heavy-duty applications. Too coarse to compact into a smooth surface, but unmatched for erosion control, rip-rap, structural fill, and rugged base layers where bulk and stability matter most.',
    image:
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=400&fit=crop',
    products: [
      {
        name: '2-4" Quarry Spalls',
        slug: '2-4-quarry-rock',
        description:
          'Large, angular quarry stone ranging from 2 to 4 inches. Too big to compact into a smooth surface, but excellent for erosion control on slopes and embankments, rip-rap along ditches and waterways, heavy-duty fill in wet areas, and as a rugged base layer for construction access roads. A tough, utilitarian rock for jobs that need serious bulk and stability.',
        image:
          'https://cdn.magicpatterns.com/uploads/4UjtVoq8sViWWuG31jax7Z/2-4_querry_rocks.png',
        images: [
          'https://cdn.magicpatterns.com/uploads/4UjtVoq8sViWWuG31jax7Z/2-4_querry_rocks.png',
          'https://cdn.magicpatterns.com/uploads/aUsbs8gj6VDBChPjU1Zum7/image.png',
          'https://cdn.magicpatterns.com/uploads/3Y3q3VVY27CmEuYorBt4P9/2_4_inch_rock.png',
        ],
        pricePerYard: 70,
      },
      {
        name: '4-8" Quarry Spalls',
        slug: '4-8-quarry-spalls',
        description:
          'Extra-large angular quarry stone ranging from 4 to 8 inches. Built for the toughest jobs — heavy rip-rap along streams and shorelines, large-scale erosion control on steep embankments, structural backfill behind retaining walls, and armoring drainage outfalls. The substantial size and sharp edges lock together under their own weight, creating a durable, interlocking barrier that resists displacement from water and gravity.',
        image:
          'https://cdn.magicpatterns.com/uploads/4UjtVoq8sViWWuG31jax7Z/2-4_querry_rocks.png',
        pricePerYard: 70,
      },
      {
        name: '2" Ballast',
        slug: '2-inch-ballast',
        description:
          'Uniformly sized, angular crushed stone at approximately 2 inches. Originally specified for railroad track beds, ballast rock is prized for its consistent sizing and excellent load distribution. Ideal for heavy-duty base layers under structures, stabilizing soft ground for equipment access, French drain systems that need high flow capacity, and as a clean structural fill. The uniform size creates predictable void space for drainage while providing solid bearing strength.',
        image:
          'https://cdn.magicpatterns.com/uploads/aUsbs8gj6VDBChPjU1Zum7/image.png',
        pricePerYard: 55,
      },
      {
        name: 'Large Rocks',
        slug: 'large-rocks',
        description:
          'Massive 1-man to 4-man quarry rocks for heavy-duty erosion control, retaining features, and structural armoring. These oversized stones range from roughly 100 lbs up to 2,000+ lbs and are ideal for rip-rap on steep banks, shoreline protection, large retaining walls, and anywhere you need serious mass to hold ground. Delivery includes placement with our equipment. Call for availability and sizing.',
        image:
          'https://cdn.magicpatterns.com/uploads/4UjtVoq8sViWWuG31jax7Z/2-4_querry_rocks.png',
        pricePerYard: 125,
      },
    ],
  },
]
